import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, withModifiers as _withModifiers, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "flex items-center justify-between mb-6" }
const _hoisted_3 = { class: "mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3" }
const _hoisted_4 = { class: "relative flex-1" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center justify-center py-16 bg-white border border-gray-200 rounded-xl"
}
const _hoisted_6 = {
  key: 1,
  class: "grid grid-cols-1 lg:grid-cols-2 gap-4"
}
const _hoisted_7 = { class: "flex items-start justify-between gap-3" }
const _hoisted_8 = { class: "min-w-0" }
const _hoisted_9 = { class: "flex items-center gap-2" }
const _hoisted_10 = { class: "font-semibold text-[#22282F] truncate" }
const _hoisted_11 = {
  key: 0,
  class: "text-xs text-gray-500 mt-1 line-clamp-2"
}
const _hoisted_12 = { class: "flex items-center gap-1 shrink-0" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "overflow-x-auto -mx-1 px-1" }
const _hoisted_17 = { class: "flex items-center gap-1.5 min-w-max py-1" }
const _hoisted_18 = { class: "flex items-center gap-1.5 flex-wrap pt-3 border-t border-gray-100" }
const _hoisted_19 = ["title"]
const _hoisted_20 = {
  key: 0,
  class: "text-xs text-gray-400 italic"
}
const _hoisted_21 = {
  key: 2,
  class: "bg-white border border-gray-200 rounded-xl p-16 text-center shadow-sm"
}
const _hoisted_22 = { class: "text-sm text-gray-500 mb-5" }
const _hoisted_23 = { class: "bg-white rounded-xl max-w-sm w-full p-6 shadow-xl" }
const _hoisted_24 = { class: "flex items-center gap-3 mb-4" }
const _hoisted_25 = { class: "text-lg font-bold text-[#22282F]" }
const _hoisted_26 = { class: "text-sm text-gray-600 mb-6" }
const _hoisted_27 = { class: "flex gap-3" }

import { ref, computed, onMounted, reactive } from 'vue'
import { useRouter } from 'vue-router'
import {
  Plus, Search, Edit3, Trash2, Copy, ArrowRight, AlertTriangle,
  Workflow, MapPin, Shield, GitBranch,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import DocumentTypeVisibilityPanel from '@/components/DocumentTypeVisibilityPanel.vue'
import DocumentTypeAccessExceptionsPanel from '@/components/DocumentTypeAccessExceptionsPanel.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { ROLE_NATIONAL_LABELS, type WorkflowStep, type WorkflowTemplate } from '@/types/workflowTemplate'


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkflowTemplatesView',
  setup(__props) {

const router = useRouter()
const toast = useToast()

const loading = ref(false)
const items = ref<WorkflowTemplate[]>([])
const searchQuery = ref('')
const filterStatus = ref<'all' | 'active' | 'inactive'>('all')

const confirmModal = reactive({
  show: false,
  type: 'delete' as 'delete' | 'duplicate',
  title: '',
  message: '',
  confirmLabel: '',
  onConfirm: () => {},
})

const filtered = computed(() => {
  let list = items.value
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(wt => wt.nom.toLowerCase().includes(q))
  }
  if (filterStatus.value === 'active') list = list.filter(wt => wt.is_active)
  else if (filterStatus.value === 'inactive') list = list.filter(wt => !wt.is_active)
  return list
})

const fetchAll = async () => {
  loading.value = true
  try {
    // La liste n'inclut pas les étapes (steps_count) : on récupère le détail
    // de chacun pour l'aperçu visuel — le nombre de workflows reste modeste.
    const summaries = await apiFetch<WorkflowTemplate[]>('/api/admin/workflow-templates/')
    const details = await Promise.all(
      summaries.map(s => apiFetch<WorkflowTemplate>(`/api/admin/workflow-templates/${s.id}/`))
    )
    items.value = details
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger les workflows')
  } finally {
    loading.value = false
  }
}

const stepIcon = (step: WorkflowStep) => {
  if (step.kind === 'HIERARCHIE_INITIATEUR') return GitBranch
  if (step.kind === 'ROLE_NATIONAL') return Shield
  return MapPin
}

const stepChipClass = (step: WorkflowStep) => {
  if (step.kind === 'HIERARCHIE_INITIATEUR') return 'bg-violet-50 text-violet-700 border-violet-100'
  if (step.kind === 'ROLE_NATIONAL') return 'bg-amber-50 text-amber-700 border-amber-100'
  return 'bg-blue-50 text-blue-700 border-blue-100'
}

const stepLabel = (step: WorkflowStep) => {
  if (step.kind === 'HIERARCHIE_INITIATEUR') return "Hiérarchie de l'initiateur"
  if (step.kind === 'ROLE_NATIONAL') return ROLE_NATIONAL_LABELS[step.role_national_code || ''] || step.role_national_code
  return step.target?.label || 'Cible manquante'
}

const askDelete = (wt: WorkflowTemplate) => {
  // Le serveur refuse de supprimer un workflow qu'une règle de profil utilise : on le dit
  // tout de suite plutôt que de faire confirmer une suppression vouée à l'échec.
  const parRegle = wt.document_types.filter(d => d.via_regle)
  if (parRegle.length) {
    toast.error(
      'Suppression impossible',
      `« ${wt.nom} » est utilisé par une règle de profil de : ${parRegle.map(d => d.nom).join(', ')}. Retirez ces règles dans la fiche du type de document avant de le supprimer.`
    )
    return
  }
  confirmModal.type = 'delete'
  confirmModal.title = 'Supprimer ce workflow ?'
  confirmModal.message = wt.document_types.length
    ? `« ${wt.nom} » est utilisé par ${wt.document_types.length} type(s) de document (${wt.document_types.map(d => d.nom).join(', ')}) — ils ne proposeront plus de workflow prédéfini après suppression.`
    : `La suppression de « ${wt.nom} » est irréversible.`
  confirmModal.confirmLabel = 'Supprimer'
  confirmModal.onConfirm = () => doDelete(wt)
  confirmModal.show = true
}

const doDelete = async (wt: WorkflowTemplate) => {
  try {
    await apiFetch(`/api/admin/workflow-templates/${wt.id}/`, { method: 'DELETE' })
    items.value = items.value.filter(w => w.id !== wt.id)
    toast.success('Workflow supprimé', `« ${wt.nom} » a été supprimé`)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de supprimer ce workflow')
  }
}

const askDuplicate = (wt: WorkflowTemplate) => {
  confirmModal.type = 'duplicate'
  confirmModal.title = 'Dupliquer ce workflow ?'
  confirmModal.message = `Une copie « ${wt.nom} (copie) » sera créée avec les mêmes étapes, désactivée par défaut, sans association à un type de document.`
  confirmModal.confirmLabel = 'Dupliquer'
  confirmModal.onConfirm = () => doDuplicate(wt)
  confirmModal.show = true
}

const doDuplicate = async (wt: WorkflowTemplate) => {
  try {
    const clone = await apiFetch<WorkflowTemplate>(`/api/admin/workflow-templates/${wt.id}/duplicate/`, { method: 'POST' })
    router.push(`/workflow-templates/${clone.id}`)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de dupliquer ce workflow')
  }
}

onMounted(fetchAll)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[8] || (_cache[8] = _createElementVNode("div", null, [
            _createElementVNode("h2", { class: "text-2xl font-bold text-[#22282F]" }, "Workflows prédéfinis"),
            _createElementVNode("p", { class: "text-sm text-gray-500 mt-1" }, " Circuits de validation réutilisables, associables aux types de document ")
          ], -1)),
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push('/workflow-templates/new'))),
            class: "px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors flex items-center gap-2 shrink-0"
          }, [
            _createVNode(_unref(Plus), { class: "w-4 h-4" }),
            _cache[7] || (_cache[7] = _createTextVNode(" Nouveau workflow "))
          ])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(Search), { class: "absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" }),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              placeholder: "Rechercher un workflow...",
              class: "w-full pl-12 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
            }, null, 512), [
              [_vModelText, searchQuery.value]
            ])
          ]),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filterStatus).value = $event)),
            class: "px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] bg-white"
          }, _cache[9] || (_cache[9] = [
            _createElementVNode("option", { value: "all" }, "Tous statuts", -1),
            _createElementVNode("option", { value: "active" }, "Actifs", -1),
            _createElementVNode("option", { value: "inactive" }, "Inactifs", -1)
          ]), 512), [
            [_vModelSelect, filterStatus.value]
          ])
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[10] || (_cache[10] = [
              _createElementVNode("div", { class: "w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" }, null, -1)
            ])))
          : (filtered.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filtered.value, (wt) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: wt.id,
                    class: "bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4"
                  }, [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                          _createElementVNode("h3", _hoisted_10, _toDisplayString(wt.nom), 1),
                          _createElementVNode("span", {
                            class: _normalizeClass([
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0',
                    wt.is_active ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                  ])
                          }, [
                            _createElementVNode("span", {
                              class: _normalizeClass(['w-1.5 h-1.5 rounded-full', wt.is_active ? 'bg-green-500' : 'bg-red-400'])
                            }, null, 2),
                            _createTextVNode(" " + _toDisplayString(wt.is_active ? 'Actif' : 'Inactif'), 1)
                          ], 2)
                        ]),
                        (wt.description)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(wt.description), 1))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (_unref(router).push(`/workflow-templates/${wt.id}`)),
                          class: "p-2 hover:bg-gray-100 rounded-lg transition-colors",
                          title: "Modifier"
                        }, [
                          _createVNode(_unref(Edit3), { class: "w-4 h-4 text-gray-500" })
                        ], 8, _hoisted_13),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (askDuplicate(wt)),
                          class: "p-2 hover:bg-gray-100 rounded-lg transition-colors",
                          title: "Dupliquer"
                        }, [
                          _createVNode(_unref(Copy), { class: "w-4 h-4 text-gray-500" })
                        ], 8, _hoisted_14),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (askDelete(wt)),
                          class: "p-2 hover:bg-red-50 rounded-lg transition-colors",
                          title: "Supprimer"
                        }, [
                          _createVNode(_unref(Trash2), { class: "w-4 h-4 text-red-500" })
                        ], 8, _hoisted_15)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("div", _hoisted_17, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(wt.steps, (step, idx) => {
                          return (_openBlock(), _createElementBlock(_Fragment, {
                            key: step.id ?? idx
                          }, [
                            _createElementVNode("div", {
                              class: _normalizeClass([
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap border',
                    stepChipClass(step)
                  ])
                            }, [
                              (_openBlock(), _createBlock(_resolveDynamicComponent(stepIcon(step)), { class: "w-3.5 h-3.5 shrink-0" })),
                              _createTextVNode(" " + _toDisplayString(stepLabel(step)), 1)
                            ], 2),
                            (idx < wt.steps.length - 1)
                              ? (_openBlock(), _createBlock(_unref(ArrowRight), {
                                  key: 0,
                                  class: "w-3.5 h-3.5 text-gray-300 shrink-0"
                                }))
                              : _createCommentVNode("", true)
                          ], 64))
                        }), 128))
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_18, [
                      _cache[11] || (_cache[11] = _createElementVNode("span", { class: "text-xs text-gray-400 mr-1" }, "Utilisé par :", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(wt.document_types, (dt) => {
                        return (_openBlock(), _createElementBlock("span", {
                          key: dt.id,
                          class: "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600",
                          title: dt.via_regle ? 'Utilisé par une règle de profil de ce type' : undefined
                        }, [
                          _createTextVNode(_toDisplayString(dt.nom), 1),
                          (dt.via_regle)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(" (par profil)")
                              ], 64))
                            : _createCommentVNode("", true)
                        ], 8, _hoisted_19))
                      }), 128)),
                      (!wt.document_types.length)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_20, "Aucun type de document associé"))
                        : _createCommentVNode("", true)
                    ])
                  ]))
                }), 128))
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_21, [
                _createVNode(_unref(Workflow), { class: "w-14 h-14 text-gray-200 mx-auto mb-4" }),
                _cache[13] || (_cache[13] = _createElementVNode("h3", { class: "text-lg font-semibold text-[#22282F] mb-1" }, "Aucun workflow prédéfini", -1)),
                _createElementVNode("p", _hoisted_22, _toDisplayString(searchQuery.value || filterStatus.value !== 'all' ? 'Aucun résultat pour ces filtres' : "Créez un circuit type pour l'associer à un type de document"), 1),
                (!searchQuery.value && filterStatus.value === 'all')
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(router).push('/workflow-templates/new'))),
                      class: "inline-flex items-center gap-2 px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 transition-colors"
                    }, [
                      _createVNode(_unref(Plus), { class: "w-4 h-4" }),
                      _cache[12] || (_cache[12] = _createTextVNode(" Créer le premier workflow "))
                    ]))
                  : _createCommentVNode("", true)
              ])),
        _createVNode(DocumentTypeVisibilityPanel),
        _createVNode(DocumentTypeAccessExceptionsPanel)
      ]),
      (confirmModal.show)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50",
            onClick: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (confirmModal.show = false), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_23, [
              _createElementVNode("div", _hoisted_24, [
                _createElementVNode("div", {
                  class: _normalizeClass(['p-2.5 rounded-full', confirmModal.type === 'delete' ? 'bg-red-100' : 'bg-blue-100'])
                }, [
                  (confirmModal.type === 'delete')
                    ? (_openBlock(), _createBlock(_unref(AlertTriangle), {
                        key: 0,
                        class: "w-5 h-5 text-red-600"
                      }))
                    : (_openBlock(), _createBlock(_unref(Copy), {
                        key: 1,
                        class: "w-5 h-5 text-blue-600"
                      }))
                ], 2),
                _createElementVNode("h3", _hoisted_25, _toDisplayString(confirmModal.title), 1)
              ]),
              _createElementVNode("p", _hoisted_26, _toDisplayString(confirmModal.message), 1),
              _createElementVNode("div", _hoisted_27, [
                _createElementVNode("button", {
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (confirmModal.show = false)),
                  class: "flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                }, " Annuler "),
                _createElementVNode("button", {
                  onClick: _cache[5] || (_cache[5] = ($event: any) => {confirmModal.onConfirm(); confirmModal.show = false}),
                  class: _normalizeClass([
              'flex-1 px-4 py-2.5 rounded-lg text-sm text-white transition-colors',
              confirmModal.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#22282F] hover:bg-[#22282F]/90'
            ])
                }, _toDisplayString(confirmModal.confirmLabel), 3)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})